import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { ErrorState, LoadingState, Panel, StatusBadge } from '../components/ui.js';

/** Staging vs. Produkce: co se liší (jen odesílání) a kontrola připravenosti k přepnutí (D-321). */
interface Data {
  current: string | null;
  sharedDatabase: boolean;
  differences: Array<{ area: string; staging: string; production: string }>;
  checks: Array<{ key: string; label: string; status: 'ok' | 'warn' | 'block'; detail: string }>;
}

const TONE = { ok: 'ok', warn: 'warn', block: 'bad' } as const;
const TEXT = { ok: 'v pořádku', warn: 'zkontrolovat', block: 'blokuje' } as const;

export function EnvironmentDifferences(): JSX.Element {
  const q = useQuery({
    queryKey: ['environment-differences'],
    queryFn: () => api.get<Data>('/api/environment/differences'),
  });
  if (q.isLoading) return <LoadingState what="rozdíly prostředí" />;
  if (q.isError || !q.data)
    return <ErrorState title="Rozdíly prostředí se nepodařilo načíst" error={q.error} />;
  const d = q.data;
  return (
    <>
      <Panel title="Zkouška a Produkce: co se liší">
        <p className="note">
          Zkouška umí skoro vše jako Produkce a liší se <b>jen odesíláním</b>: e-mail ze Zkoušky
          odejde výhradně na adresy z allowlistu, a to pojistkou v kódu, kterou z administrace nejde
          obejít. Data jsou skutečná a{' '}
          {d.sharedDatabase ? 'sdílená s Produkcí' : 'zatím oddělená od Produkce'}.
        </p>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Oblast</th>
                <th>Zkouška</th>
                <th>Produkce</th>
              </tr>
            </thead>
            <tbody>
              {d.differences.map((r) => (
                <tr key={r.area}>
                  <td>{r.area}</td>
                  <td>{r.staging}</td>
                  <td>{r.production}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title="Připravenost k přepnutí na Produkci">
        <ul>
          {d.checks.map((c) => (
            <li key={c.key}>
              <StatusBadge tone={TONE[c.status]} icon={c.status === 'ok' ? 'check' : 'info'}>
                {TEXT[c.status]}
              </StatusBadge>{' '}
              <b>{c.label}</b>: {c.detail}
            </li>
          ))}
        </ul>
      </Panel>
    </>
  );
}
